import { DestroyRef, inject,Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import * as Sentry from '@sentry/angular';

import { DataLayerPushEvent, GTMService } from '@personal-area/global/gtm';

import { UsabillaService } from '../usabilla.service';
import { CollectUserDataService } from '../utils/collect-user-data.util';
import { isGA4Event } from '../utils/is-ga4-event.util';
import { BaseGetFeedbackEvent } from './base-event.interface';

@Injectable({ providedIn: 'root' })
export class AppEventsEvent implements BaseGetFeedbackEvent {
  private readonly _usabilla = inject(UsabillaService);
  private readonly _gtm = inject(GTMService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _userDataService = inject(CollectUserDataService);

  public init(): void {
    this._subscribeToGtmEvents();
  }

  private _subscribeToGtmEvents(): void {
    this._gtm.event$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event: DataLayerPushEvent) => {
        if (!event.event) {
          return;
        }

        const identifier = this._buildEventIdentifier(event);

        try {
          this._usabilla.executeEvent('trigger', identifier);
          this._usabilla.executeEvent('virtualPageView');
          this._usabilla.executeEvent('data', {
            custom: this._userDataService.getUserData(),
          });
        } catch (error) {
          Sentry.captureException(error, {
            tags: { component: 'GetFeedbackService' },
          });
        }
      });
  }

  private _buildEventIdentifier(event: DataLayerPushEvent): string {
    if (!isGA4Event(event.data)) {
      return event.event.toString();
    }

    const GA4Event = event.data;
    return [
      GA4Event.context,
      GA4Event.object,
      GA4Event.action,
      GA4Event.page_name || '',
      event.event,
    ]
      .join(':')
      .replace(/undefined|null/g, '');
  }
}
